import { axiosPostData } from 'domain/repository/axios/AxiosBasicImpl';
import referenceAxiosInstance from 'domain/repository/axios/referenceAxiosInstance';
import { BuConfig, ErrorReport, HealthCheckCache } from './HealthCheckConfig';

const STORAGE_KEY = 'config_health_check_cache';
const REPORT_INTERVAL = 12 * 60 * 60 * 1000; // 12 HOURS
const ERROR_TYPE = 'RAIL_CONF_MISMATCH' as const;

/**
 * Generate a conversation ID
 */
const generateSessionId = (): string => {
    const existingId = sessionStorage.getItem('health_check_session_id');
    if (existingId) {
        return existingId;
    }

    const newId = 'hc_' + Date.now().toString(36) +
        Math.random().toString(36).substring(2, 9);

    sessionStorage.setItem('health_check_session_id', newId);
    return newId;
};

const sessionId: string = generateSessionId();

/**
 * Deep comparison of two values
 */
const deepCompare = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (a == null || b == null) return a === b;
    if (typeof a !== typeof b) return false;
    if (typeof a === 'object') return JSON.stringify(a) === JSON.stringify(b);
    return a === b;
};

/**
 * Generate error status identifier
 */
const generateErrorState = (fieldRail: boolean | string, railEnable: boolean | string): string => {
    return `RAIL_${fieldRail}_${railEnable}`;
};

/**
 * Generate configuration hash
 */
const generateConfigHash = (buConfig: BuConfig): string => {
    const configStr = JSON.stringify({
        fieldRail: buConfig.REACT_APP_FIELD_ENABLE?.RAIL,
        railEnable: buConfig.REACT_APP_RAIL_ENABLE
    });

    let hash = 0;
    for (let i = 0; i < configStr.length; i++) {
        const char = configStr.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }

    return Math.abs(hash).toString(36).substring(0, 8);
};

/**
 * Retrieve cache
 */
const getCache = (): HealthCheckCache | null => {
    try {
        const cached = localStorage.getItem(STORAGE_KEY);
        return cached ? JSON.parse(cached) : null;
    } catch (error) {
        console.warn('Failed to read the cache:', error);
        return null;
    }
};

/**
 * Update cache
 */
const updateCache = (
    errorState: string,
    timestamp: number,
    incrementCount: boolean = true
): void => {
    try {
        const existingCache = getCache();
        const newCache: HealthCheckCache = {
            lastReportTime: timestamp,
            lastErrorState: errorState,
            reportCount: incrementCount
                ? (existingCache?.reportCount || 0) + 1
                : (existingCache?.reportCount || 0)
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(newCache));
    } catch (error) {
        console.warn('Failed to update cache:', error);
    }
};

/**
 * Clear the error cache (when the problem is resolved)
 */
const clearErrorCacheIfResolved = (): void => {
    try {
        const cache = getCache();
        if (cache) {
            if (Date.now() - cache.lastReportTime > 24 * 60 * 60 * 1000) {
                localStorage.removeItem(STORAGE_KEY);
                console.log('The old error cache has been cleared.');
            }
        }
    } catch (error) {
        console.warn('Failed to clear cache:', error);
    }
};

/**
 * Determine whether an error should be reported
 */
const shouldReportError = (
    cache: HealthCheckCache | null,
    currentErrorState: string,
    currentTime: number
): boolean => {
    if (!cache) return true;
    if (cache.lastErrorState !== currentErrorState) return true;
    if (currentTime - cache.lastReportTime >= REPORT_INTERVAL) return true;
    return false;
};

/**
 * Report the error to the backend
 */
const reportError = async (data: {
    fieldRail: boolean | string;
    railEnable: boolean | string;
    configHash: string;
    buConfig: BuConfig;
}): Promise<void> => {
    const { fieldRail, railEnable, configHash, buConfig } = data;

    const report: ErrorReport = {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        errorType: ERROR_TYPE,
        currentValues: {
            REACT_APP_FIELD_ENABLE_RAIL: fieldRail,
            REACT_APP_RAIL_ENABLE: railEnable
        },
        errorMessage: `Configuration inconsistency: REACT_APP_FIELD_ENABLE.RAIL=${fieldRail}, REACT_APP_RAIL_ENABLE=${railEnable}`,
        sessionId: sessionId,
        configHash
    };

    console.log('Report erroneous data:', report);

    axiosPostData(referenceAxiosInstance, `/v1/config/health-check/report`, report);

};

/**
 * Record skipped reports
 */
const logSkippedReport = (
    cache: HealthCheckCache | null,
    errorState: string,
    currentTime: number
): void => {
    if (!cache) return;

    const remainingTime = Math.max(0, REPORT_INTERVAL - (currentTime - cache.lastReportTime));
    const hours = Math.floor(remainingTime / (60 * 60 * 1000));
    const minutes = Math.floor((remainingTime % (60 * 60 * 1000)) / (60 * 1000));

    console.log(`The next reporting deadline: ${hours} hours ${minutes} minutes`);
};

/**
 * Handling the situation of inconsistent configurations
 */
const handleMismatch = async (
    buConfig: BuConfig,
    fieldRail: boolean | string,
    railEnable: boolean | string
): Promise<void> => {
    const currentErrorState = generateErrorState(fieldRail, railEnable);
    const configHash = generateConfigHash(buConfig);
    const cache = getCache();
    const now = Date.now();
    const shouldReport = shouldReportError(cache, currentErrorState, now);

    if (shouldReport) {
        try {
            await reportError({
                fieldRail,
                railEnable,
                configHash,
                buConfig
            });
            updateCache(currentErrorState, now);
            console.log('The error report has been submitted');
        } catch (reportError) {
            console.error('Error report failed:', reportError);
            updateCache(currentErrorState, now, false);
        }
    } else {
        console.log('The same error has already been reported within 12 hours. Skipping this report');
        logSkippedReport(cache, currentErrorState, now);
    }
};

/**
 * Conduct health check-ups
 */
export const healthCheck = async (buConfig: BuConfig | null): Promise<void> => {
    if (!buConfig) {
        console.warn('Configuration is empty. Skip the health check');
        return;
    }

    try {
        const fieldRail = buConfig.REACT_APP_FIELD_ENABLE?.RAIL;
        const railEnable = buConfig.REACT_APP_RAIL_ENABLE;

        if (fieldRail === undefined || railEnable === undefined) {
            console.warn('The configuration field does not exist, so the check is skipped');
            return;
        }

        const isSame = deepCompare(fieldRail, railEnable);

        if (!isSame) {
            console.log('A configuration inconsistency has been detected. A report is being prepared');
            handleMismatch(buConfig, fieldRail, railEnable);
        } else {
            console.log('Configuration check passed, fields are consistent');
            clearErrorCacheIfResolved();
        }
    } catch (error) {
        console.error('Health check failed:', error);
    }
};
